import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Button from "@mui/material/Button";
import { grey } from "@mui/material/colors";
import EditOutlined from "@mui/icons-material/EditOutlined";
import isNil from "lodash/isNil";
import { useDispatch, useSelector } from "react-redux";

import BasicModal from "../../modals/BasicModal";
import SelectInput from "../../inputs/SelectInput";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import { useLoanTermsHook } from "../../../hooks/useLoanTermsHook";
import {
  loanFetchedDataFundingEntitiesSet,
  loanFetchedDataLoanTermsSet,
} from "../../../redux/reducers/loanFetchedDataReducer";

/**
 * "Set Funding Entity" — the ONE modal for assigning a loan's funding entity.
 *
 * Extracted from `LoanDrilldownLoanTermsTab`, which owned it privately. It is now
 * shared because the Wire & Funding tab gates itself on the funding entity being
 * set and offers to set it in place: two entry points, and they have to be the
 * same dialog writing through the same endpoint, not a lookalike.
 *
 * Self-contained on purpose — it loads the entity list itself (cache-gated on the
 * `loanFetchedData.fundingEntities` slice) and performs the write, so a caller
 * only supplies `dealId` and gets `onSaved` back with the fresh loan terms. The
 * Loan Terms tab, which already has both in hand, can still pass them in.
 */
const SetFundingEntityModal = ({
  show,
  onClose,
  dealId,
  currentEntityId,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const { loading, getFundingEntities, updateFundingEntity } =
    useLoanTermsHook();
  const fundingEntities = useSelector(
    (state) => state?.loanFetchedData?.fundingEntities,
  );

  const [pending, setPending] = useState(currentEntityId || "");

  useEffect(() => {
    if (show) setPending(currentEntityId || "");
  }, [show, currentEntityId]);

  // Load the list on first open if nothing has fetched it yet — the Loan Terms
  // tab populates this slice on mount, but the Wire & Funding gate may be the
  // first surface a closer lands on.
  useEffect(() => {
    if (!show || !isNil(fundingEntities)) return;
    getFundingEntities({
      onSuccessFn: (data) => dispatch(loanFetchedDataFundingEntitiesSet(data)),
      onFailFn: () => dispatch(loanFetchedDataFundingEntitiesSet([])),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, fundingEntities]);

  const options = useMemo(
    () =>
      (fundingEntities || []).map((e) => ({
        value: e?._id,
        label: e?.name,
      })),
    [fundingEntities],
  );

  const handleSave = () => {
    // The endpoint takes the whole entity object, not just its id.
    const fullData = (fundingEntities || []).find((e) => e?._id === pending);
    if (isNil(fullData)) return;
    updateFundingEntity({
      dealId,
      data: fullData,
      onSuccessFn: (data) => {
        dispatch(loanFetchedDataLoanTermsSet(data));
        if (onSaved) onSaved(data);
        onClose();
      },
    });
  };

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title="Set Funding Entity"
      maxWidth="xs"
      icon={<EditOutlined className="thin" />}
    >
      <Flx column g={1.75}>
        <Txt sx={{ fontSize: 13, color: grey[700] }}>
          Choose the funding entity for this loan.
        </Txt>
        <SelectInput
          value={pending}
          onChange={(v) => setPending(v)}
          options={options}
          placeholder="Select funding entity"
          fullWidth
        />
        <Flx jb ac sx={{ mt: 0.5 }}>
          <Button onClick={onClose} variant="text" disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!pending || pending === currentEntityId || loading}
            onClick={handleSave}
          >
            Save
          </Button>
        </Flx>
      </Flx>
    </BasicModal>
  );
};

SetFundingEntityModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  dealId: PropTypes.string,
  currentEntityId: PropTypes.string,
  onSaved: PropTypes.func,
};

export default SetFundingEntityModal;
